import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Target, TrendUp, TrendDown, X, Trash, MagnifyingGlass, ArrowsClockwise, ChartLine } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AXIOS = (extra = {}) => ({
  headers: authHeaders(),
  withCredentials: true,
  timeout: 12000,
  ...extra,
});

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtNum = (n, dec = 2) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('es-ES', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

/**
 * Modal Historial de Rotura de Nivel (Auto-SR).
 * Lista todos los eventos con source='auto_sr' del usuario, filtrables por
 * ticker.  Permite borrar individualmente cada uno o vaciar todo el histórico
 * (solo los auto_sr — no afecta a los eventos de niveles marcados manualmente).
 */
export default function AutoSRHistoryModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [tickerFilter, setTickerFilter] = useState('all');
  const [dirFilter, setDirFilter] = useState('all'); // all | up | down
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/user/level-events`, AXIOS());
      const all = res.data?.level_events || [];
      const autoSr = all.filter((e) => e.source === 'auto_sr');
      autoSr.sort((a, b) => (b.triggered_at || '').localeCompare(a.triggered_at || ''));
      setEvents(autoSr);
    } catch (e) {
      // silencioso
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const tickers = useMemo(() => {
    const set = new Set(events.map((e) => e.ticker).filter(Boolean));
    return Array.from(set).sort();
  }, [events]);

  const filtered = useMemo(() => events.filter((e) => {
    if (tickerFilter !== 'all' && e.ticker !== tickerFilter) return false;
    if (dirFilter !== 'all' && e.direction !== dirFilter) return false;
    return true;
  }), [events, tickerFilter, dirFilter]);

  const deleteOne = async (id) => {
    if (!id) return;
    setBusy(true);
    setEvents((prev) => prev.filter((e) => e.id !== id));
    try {
      await axios.delete(`${API}/user/level-events/${id}`, AXIOS());
    } catch { load(); } finally { setBusy(false); }
  };

  const deleteAll = async () => {
    const count = filtered.length;
    if (!count) return;
    const scopeLabel = tickerFilter === 'all' ? 'todo el historial de rotura de nivel' : `los ${count} eventos de ${tickerFilter}`;
    if (!window.confirm(`¿Borrar ${scopeLabel}? Esta acción no se puede deshacer.`)) return;
    setBusy(true);
    try {
      const params = { source: 'auto_sr' };
      if (tickerFilter !== 'all') params.ticker = tickerFilter;
      await axios.delete(`${API}/user/level-events`, AXIOS({ params }));
      await load();
    } catch { /* ignora */ } finally { setBusy(false); }
  };

  // Abre el gráfico del ticker y dibuja una línea horizontal EN EL NIVEL EXACTO
  // donde se produjo la rotura. Cierra el modal para que el usuario vea el chart.
  const openInChart = (ev) => {
    if (!ev?.ticker) return;
    // 1) Cambiar el ticker principal del gráfico
    window.dispatchEvent(new CustomEvent('openTickerChart', {
      detail: { ticker: ev.ticker, name: ev.ticker },
    }));
    // 2) Enviar el nivel a resaltar (AdvancedStockChart lo dibuja como ReferenceLine)
    window.dispatchEvent(new CustomEvent('kapital-highlight-sr-level', {
      detail: {
        ticker: ev.ticker,
        level: ev.level,
        kind: ev.kind,
        direction: ev.direction,
        note: ev.note,
        triggered_at: ev.triggered_at,
      },
    }));
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="auto-sr-history-backdrop"
    >
      <div
        className="w-full max-w-3xl rounded-2xl border overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(160deg, rgba(6,9,16,0.98), rgba(3,6,12,0.98))',
          border: '1px solid rgba(0,220,255,0.35)',
          boxShadow: '0 0 40px rgba(0,220,255,0.15), 0 24px 60px rgba(0,0,0,0.7)',
          maxHeight: '85vh',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="auto-sr-history-modal"
      >
        {/* Cabecera */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gold-500/20">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: 'rgba(0,220,255,0.12)', border: '1px solid rgba(0,220,255,0.4)' }}>
              <Target size={14} weight="fill" className="text-gold-300" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gold-200">
                Historial · Rotura de Nivel
              </div>
              <div className="text-[10px] text-zinc-500 truncate">
                Todos los cruces de soportes/resistencias auto-detectados en tu cartera
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={load}
              disabled={loading || busy}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-gold-300 transition-all disabled:opacity-40"
              title="Recargar"
              data-testid="auto-sr-history-reload"
            >
              <ArrowsClockwise size={13} weight="bold" className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all"
              title="Cerrar"
              data-testid="auto-sr-history-close"
            >
              <X size={13} weight="bold" />
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="px-4 py-3 border-b border-zinc-800/70 flex flex-wrap items-center gap-2">
          {/* Filtro ticker */}
          <div className="flex items-center gap-1.5">
            <MagnifyingGlass size={11} className="text-zinc-500" />
            <select
              value={tickerFilter}
              onChange={(e) => setTickerFilter(e.target.value)}
              className="text-[11px] bg-zinc-900 border border-zinc-700 hover:border-gold-500/60 focus:border-gold-500 rounded px-2 py-1 text-white outline-none font-mono"
              data-testid="auto-sr-history-ticker-filter"
            >
              <option value="all">Todos los tickers ({events.length})</option>
              {tickers.map((tk) => (
                <option key={tk} value={tk}>
                  {tk} ({events.filter((e) => e.ticker === tk).length})
                </option>
              ))}
            </select>
          </div>

          {/* Filtro dirección */}
          <div className="flex items-center gap-0.5 rounded bg-zinc-900 border border-zinc-800 p-0.5" role="group">
            {[
              { k: 'all', label: 'Ambos' },
              { k: 'up', label: 'Alcistas', icon: TrendUp, color: '#00ff6a' },
              { k: 'down', label: 'Bajistas', icon: TrendDown, color: '#ff4444' },
            ].map((opt) => {
              const active = dirFilter === opt.k;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setDirFilter(opt.k)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${
                    active ? 'bg-gold-500/20 text-gold-200' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                  data-testid={`auto-sr-history-dir-${opt.k}`}
                >
                  {Icon && <Icon size={10} weight="bold" style={{ color: active ? opt.color : undefined }} />}
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="flex-1" />

          {/* Contador + borrar */}
          <span className="text-[10px] text-zinc-500 font-mono">
            {filtered.length} evento{filtered.length === 1 ? '' : 's'}
          </span>
          {filtered.length > 0 && (
            <button
              type="button"
              onClick={deleteAll}
              disabled={busy}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-red-400 hover:text-red-200 hover:bg-red-500/10 border border-transparent hover:border-red-500/40 transition-all disabled:opacity-40"
              data-testid="auto-sr-history-delete-all"
            >
              <Trash size={10} weight="bold" />
              {tickerFilter === 'all' ? 'Borrar todo' : `Borrar ${tickerFilter}`}
            </button>
          )}
        </div>

        {/* Lista scrollable */}
        <div className="flex-1 overflow-y-auto px-3 py-2" data-testid="auto-sr-history-list">
          {loading && (
            <div className="text-center py-8 text-[11px] text-zinc-500">Cargando historial…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-10">
              <Target size={28} weight="duotone" className="text-gold-500/40 mx-auto mb-2" />
              <div className="text-xs text-zinc-400">Aún no hay rupturas detectadas</div>
              <div className="text-[10px] text-zinc-600 mt-1">
                {events.length === 0
                  ? 'Cuando el precio de un activo de tu cartera cruce un soporte o resistencia auto-detectado, aparecerá aquí.'
                  : 'No hay eventos con los filtros seleccionados.'}
              </div>
            </div>
          )}
          {!loading && filtered.map((ev) => {
            const up = ev.direction === 'up';
            const accent = up ? '#00ff6a' : '#ff4444';
            const isBreakout = ev.note?.startsWith('🚀');
            return (
              <div
                key={ev.id}
                className="mb-2 rounded-lg border p-2.5 transition-colors hover:bg-white/[0.03]"
                style={{
                  background: 'rgba(9,9,11,0.6)',
                  borderColor: `${accent}30`,
                }}
                data-testid={`auto-sr-event-${ev.id}`}
              >
                <div className="flex items-start gap-2.5">
                  {/* Icono direccional */}
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${accent}18`, border: `1px solid ${accent}55` }}
                  >
                    {up
                      ? <TrendUp size={14} weight="bold" style={{ color: accent }} />
                      : <TrendDown size={14} weight="bold" style={{ color: accent }} />}
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span
                        className="font-mono font-black text-sm tabular-nums"
                        style={{ color: accent, textShadow: `0 0 6px ${accent}70` }}
                      >
                        {ev.ticker}
                      </span>
                      <span
                        className="text-[9px] uppercase tracking-widest font-bold px-1 py-0.5 rounded"
                        style={{
                          color: accent, background: `${accent}18`, border: `1px solid ${accent}55`,
                        }}
                      >
                        {isBreakout ? '★ ROTURA' : 'RETEST'} · {(ev.kind || '').toUpperCase()}
                      </span>
                      <span className="text-[10px] text-zinc-600 font-mono ml-auto shrink-0">
                        {fmtDate(ev.triggered_at)}
                      </span>
                    </div>

                    <div className="text-[11px] text-zinc-300 leading-snug mb-1">
                      {ev.note || (up ? 'Cruce alcista' : 'Cruce bajista')}
                    </div>

                    <div className="flex items-center gap-3 text-[10px] font-mono tabular-nums text-zinc-400">
                      <span>
                        <span className="text-zinc-600">Nivel:</span>{' '}
                        <span className="text-gold-300">{fmtNum(ev.level, 2)}</span>
                      </span>
                      <span>
                        <span className="text-zinc-600">Precio cruce:</span>{' '}
                        <span style={{ color: accent }}>{fmtNum(ev.price, 2)}</span>
                      </span>
                      {Number.isFinite(Number(ev.price)) && Number.isFinite(Number(ev.level)) && Number(ev.level) > 0 && (
                        <span>
                          <span className="text-zinc-600">Δ:</span>{' '}
                          <span className="text-zinc-300">
                            {((Number(ev.price) / Number(ev.level) - 1) * 100).toFixed(2)}%
                          </span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="shrink-0 flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => openInChart(ev)}
                      disabled={busy}
                      className="p-1.5 rounded text-gold-400 hover:text-gold-200 hover:bg-gold-500/15 border border-transparent hover:border-gold-500/40 transition-all disabled:opacity-40"
                      title="Abrir el gráfico del activo con la línea del nivel dibujada"
                      data-testid={`auto-sr-event-chart-${ev.id}`}
                    >
                      <ChartLine size={11} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteOne(ev.id)}
                      disabled={busy}
                      className="p-1.5 rounded text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-40"
                      title="Eliminar este evento"
                      data-testid={`auto-sr-event-delete-${ev.id}`}
                    >
                      <Trash size={11} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pie */}
        <div className="px-4 py-2 border-t border-zinc-800/70">
          <div className="text-[10px] text-zinc-600 leading-snug">
            La detección corre cada 2 min sobre los activos de tu cartera con más de una acción. Puedes desactivarla desde Ajustes → Rotura de Soporte / Resistencia.
          </div>
        </div>
      </div>
    </div>
  );
}
